import { useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import api from '../api';
import { 
  validateImageFile, 
  handleApiError
} from '../utils/imageValidation';

export const useImageProcessing = (selectedDate, setMacroResult, setError) => {
  const [imageLoading, setImageLoading] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [cameraResolution, setCameraResolution] = useState({ width: 640, height: 480 });
  
  const webcamRef = useRef();
  const fileInputRef = useRef();

  const cameraResOptions = [
    { label: 'Standard (640x480)', value: { width: 640, height: 480 } },
    { label: 'HD (1280x720)', value: { width: 1280, height: 720 } },
    { label: 'Full HD (1920x1080)', value: { width: 1920, height: 1080 } },
  ];  // Handle camera capture
  const handleCapture = async () => {
    if (!webcamRef.current) {
      setError('Camera not available. Please try again or use the upload option.');
      return;
    }

    let imageSrc;
    try {
      imageSrc = webcamRef.current.getScreenshot();
    } catch (captureErr) {
      setError('Failed to capture image from camera. Please ensure your camera is working and try again.');
      return;
    }

    if (!imageSrc) {
      setError('Camera capture failed. Please ensure your camera is working and try again.');
      return;
    }

    setShowCameraModal(false);
    setImageLoading(true);
    setError('');
    
    try {
      // Convert base64 to blob
      const res = await fetch(imageSrc);
      const blob = await res.blob();
      
      // Validate captured image
      if (blob.size === 0) {
        setError('Camera capture failed. Please ensure your camera is working and try again.');
        return;
      }
      
      const formData = new FormData();
      formData.append('image', blob, 'capture.png');
      formData.append('date', selectedDate);
      const apiRes = await api.post('/gemini/image', formData);
      setMacroResult(apiRes.data.result);    } catch (err) {
      setError(handleApiError(err, 'Failed to analyze camera image. Please ensure the photo shows food clearly and try again.'));
    } finally {
      setImageLoading(false);
    }
  };// Handle gallery image selection via dropzone
  const onDrop = async (acceptedFiles) => {
    setShowGalleryModal(false);
    if (!acceptedFiles || acceptedFiles.length === 0) {
      setError('No valid image selected. Please choose a JPEG, PNG, GIF, WebP, or BMP image file.');
      return;
    }
    
    setImageLoading(true);
    setError('');
    
    const file = acceptedFiles[0];
    
    // Client-side validation using utility
    const validation = validateImageFile(file);
    if (!validation.isValid) {
      setError(validation.errors[0]);
      setImageLoading(false);
      return;
    }
    
    const formData = new FormData();
    formData.append('image', file);
    formData.append('date', selectedDate);
    
    try {
      const res = await api.post('/gemini/image', formData);
      setMacroResult(res.data.result);
    } catch (err) {
      setError(handleApiError(err, 'Failed to analyze the uploaded image. Please ensure it shows food clearly and try again.'));
    } finally {
      setImageLoading(false);
    }
  };  // Handle file input upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Client-side validation using utility
    const validation = validateImageFile(file);
    if (!validation.isValid) {
      setError(validation.errors[0]);
      e.target.value = ''; // Clear the input
      return;
    }
    
    setImageLoading(true);
    setError('');
    
    const formData = new FormData();
    formData.append('image', file);
    formData.append('date', selectedDate);
    
    try {
      const res = await api.post('/gemini/image', formData);
      setMacroResult(res.data.result);
    } catch (err) {
      setError(handleApiError(err, 'Failed to process the uploaded image. Please ensure it shows food clearly and try again.'));
    } finally {
      setImageLoading(false);
      e.target.value = ''; // Clear the input for next upload
    }
  };
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
    onDrop, 
    accept: { 
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
      'image/bmp': ['.bmp']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    maxFiles: 1,
    onDropRejected: (rejectedFiles) => {
      const rejection = rejectedFiles[0];
      if (rejection.errors.some(e => e.code === 'file-too-large')) {
        setError('Image file is too large. Please choose an image under 10MB.');
      } else if (rejection.errors.some(e => e.code === 'file-invalid-type')) {
        setError('Unsupported image format. Please use JPEG, PNG, GIF, WebP, or BMP files.');
      } else if (rejection.errors.some(e => e.code === 'too-many-files')) {
        setError('Please select only one image at a time.');
      } else {
        setError('Invalid image file. Please try a different image.');
      }
    }
  });

  const handleGallery = () => {
    setShowGalleryModal(true);
  };

  const handleCamera = () => {
    setShowCameraModal(true);
  };

  return {
    // State
    imageLoading,
    showCameraModal,
    setShowCameraModal,
    showGalleryModal,
    setShowGalleryModal,
    cameraResolution,
    setCameraResolution,
    webcamRef,
    fileInputRef,
    cameraResOptions,
    
    // Dropzone
    getRootProps,
    getInputProps,
    isDragActive,
    
    // Handlers
    handleCapture,
    handleImageUpload,
    handleGallery,
    handleCamera,
  };
};
